import axios from "axios";

// Frontend and backend are served on the same origin in every Emergent
// environment (preview, default host, and custom domains all route /api to the
// backend). Using the live origin avoids cross-origin redirects when a custom
// domain is fronted by a CDN (e.g. an apex -> www redirect), which otherwise
// blocks API calls and leaves a blank page.
const BACKEND_URL =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = {
  listMatches: () => axios.get(`${API}/matches`).then((r) => r.data),
  getMatch: (id) => axios.get(`${API}/matches/${id}`).then((r) => r.data),
  createMatch: (payload) => axios.post(`${API}/matches`, payload).then((r) => r.data),
  joinMatch: (id, name) => axios.post(`${API}/matches/${id}/join`, { name }).then((r) => r.data),
  leaveMatch: (id, name) => axios.post(`${API}/matches/${id}/leave`, { name }).then((r) => r.data),
  deleteMatch: (id) => axios.delete(`${API}/matches/${id}`).then((r) => r.data),
  addComment: (id, name, text) => axios.post(`${API}/matches/${id}/comments`, { name, text }).then((r) => r.data),
  deleteComment: (id, commentId) => axios.delete(`${API}/matches/${id}/comments/${commentId}`).then((r) => r.data),
  reactComment: (id, commentId, name, emoji) => axios.post(`${API}/matches/${id}/comments/${commentId}/react`, { name, emoji }).then((r) => r.data),
  addReply: (id, commentId, name, text) => axios.post(`${API}/matches/${id}/comments/${commentId}/replies`, { name, text }).then((r) => r.data),
  deleteReply: (id, commentId, replyId) => axios.delete(`${API}/matches/${id}/comments/${commentId}/replies/${replyId}`).then((r) => r.data),
  recordResult: (id, payload) => axios.post(`${API}/matches/${id}/result`, payload).then((r) => r.data),
  deleteResult: (id) => axios.delete(`${API}/matches/${id}/result`).then((r) => r.data),
  leaderboard: () => axios.get(`${API}/leaderboard`).then((r) => r.data),
  mvp: () => axios.get(`${API}/mvp`).then((r) => r.data),
  mvpHistory: () => axios.get(`${API}/mvp/history`).then((r) => r.data),
  pairs: () => axios.get(`${API}/pairs`).then((r) => r.data),
  confirmMatch: (id, name) => axios.post(`${API}/matches/${id}/confirm`, { name }).then((r) => r.data),
  unconfirmMatch: (id, name) => axios.post(`${API}/matches/${id}/unconfirm`, { name }).then((r) => r.data),
  playerMatches: (name) => axios.get(`${API}/players/${encodeURIComponent(name)}/matches`).then((r) => r.data),
  playerElo: (name) => axios.get(`${API}/players/${encodeURIComponent(name)}/elo`).then((r) => r.data),
  listPlayers: () => axios.get(`${API}/players`).then((r) => r.data),
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/players/photo`, fd).then((r) => r.data);
  },
  editMatch: (id, payload) => axios.put(`${API}/matches/${id}`, payload).then((r) => r.data),
  kickPlayer: (id, name) => axios.post(`${API}/matches/${id}/kick`, { name }).then((r) => r.data),
  promotePlayer: (id, name) => axios.post(`${API}/matches/${id}/promote`, { name }).then((r) => r.data),
  playerAwards: (name) => axios.get(`${API}/players/${encodeURIComponent(name)}/awards`).then((r) => r.data),
  campaignMessages: () => axios.get(`${API}/campaign/messages`).then((r) => r.data),
  campaignPost: (text) => axios.post(`${API}/campaign/messages`, { text }).then((r) => r.data),
  campaignDelete: (id) => axios.delete(`${API}/campaign/messages/${id}`).then((r) => r.data),
  campaignReact: (id, emoji) => axios.post(`${API}/campaign/messages/${id}/react`, { emoji }).then((r) => r.data),
  matchRatings: (id) => axios.get(`${API}/matches/${id}/ratings`).then((r) => r.data),
  saveRatings: (id, ratings) => axios.post(`${API}/matches/${id}/ratings`, { ratings }).then((r) => r.data),
  ratingsSummary: (name) => axios.get(`${API}/players/${encodeURIComponent(name)}/ratings-summary`).then((r) => r.data),
  ratingsBoard: () => axios.get(`${API}/ratings/leaderboard`).then((r) => r.data),
  report: (payload) => axios.post(`${API}/reports`, payload).then((r) => r.data),
  adminReports: () => axios.get(`${API}/admin/reports`).then((r) => r.data),
  adminDismissReport: (id) => axios.delete(`${API}/admin/reports/${id}`).then((r) => r.data),
  adminPlayers: () => axios.get(`${API}/admin/players`).then((r) => r.data),
  adminRenamePlayer: (name, new_name) => axios.post(`${API}/admin/players/${encodeURIComponent(name)}/rename`, { new_name }).then((r) => r.data),
  adminRemovePhoto: (name) => axios.delete(`${API}/admin/players/${encodeURIComponent(name)}/photo`).then((r) => r.data),
  adminDeletePlayerCard: (name) => axios.delete(`${API}/admin/players/${encodeURIComponent(name)}`).then((r) => r.data),
  adminRestorePlayer: (name) => axios.post(`${API}/admin/players/${encodeURIComponent(name)}/restore`).then((r) => r.data),
  adminUsers: () => axios.get(`${API}/admin/users`).then((r) => r.data),
  adminDeleteUser: (id) => axios.delete(`${API}/admin/users/${id}`).then((r) => r.data),
  adminResetPassword: (id, new_password) => axios.post(`${API}/admin/users/${id}/password`, { new_password }).then((r) => r.data),
  adminAddPlayer: (id, name) => axios.post(`${API}/admin/matches/${id}/players`, { name }).then((r) => r.data),
  adminVotes: () => axios.get(`${API}/admin/votes`).then((r) => r.data),
  adminDeleteVote: (year, category, voter_id) => axios.delete(`${API}/admin/votes`, { params: { year, category, voter_id } }).then((r) => r.data),
  adminRatings: () => axios.get(`${API}/admin/ratings`).then((r) => r.data),
  adminDeleteRating: (match_id, rater_key, target_key) => axios.delete(`${API}/admin/ratings`, { params: { match_id, rater_key, target_key } }).then((r) => r.data),
  getVotes: (year) => axios.get(`${API}/votes`, { params: year ? { year } : {} }).then((r) => r.data),
  castVote: (category, candidate, secret) => axios.post(`${API}/votes`, { category, candidate, secret }).then((r) => r.data),
  removeVote: (category) => axios.delete(`${API}/votes/${category}`).then((r) => r.data),
  resetQuestion: (username) => axios.post(`${API}/auth/reset/question`, { username }).then((r) => r.data),
  resetConfirm: (username, security_answer, new_password) =>
    axios.post(`${API}/auth/reset/confirm`, { username, security_answer, new_password }).then((r) => r.data),
};

export const photoUrl = (path) => (path ? `${API}/files/${path}` : null);

